import moment, { Moment } from 'moment';
import {MILLISECONDS_PER_DAY, MILLISECONDS_PER_HOUR} from 'constants/durations';
import {Event, WebUser} from 'types';

export function IllegalMonthError(message = undefined) {
  this.name = "IllegalMonthError";
  this.message = typeof(message) === "undefined" ? "Invalid month specified." : message;
}
IllegalMonthError.prototype = new Error();
IllegalMonthError.prototype.constructor = IllegalMonthError;

export enum CalendarDays {
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
}

export const FIRST_WEEK_DAY = CalendarDays.MONDAY;

export enum CalendarMonths {
  JANUARY = 1,
  FEBRUARY,
  MARCH,
  APRIL,
  MAY,
  JUNE,
  JULY,
  AUGUST,
  SEPTEMBER,
  OCTOBER,
  NOVEMBER,
  DECEMBER,
}

export const isEventOwner = (event: Event, currentWebUser: WebUser): boolean => currentWebUser.id === event.creator.id;

export const isSameDate = (date1: Moment, date2: Moment): boolean => date1.isSame(date2, 'day');

export const isSameMonth = (date1: Moment, date2: Moment): boolean => date1.isSame(date2, 'month');

export const isToday = (date: Moment): boolean => date.isSame(moment(), 'day');

export const formatDateToDayInMonthString = (date: Moment): string => moment(date).format('D');

export const formatDateToISOString = (date: Moment): string => moment(date).format('YYYY-MM-DD');

export const formatDateToMonthAndYearString = (date: Moment): string => date.format('MMMM YYYY');

export const formatDateToDayFullMonthAndYearString = (date: Moment): string => date.format('D MMMM YYYY');

export const formatDateToHourString = (date: Moment): string => date.format('H[h]mm');

// from node-calendar package
export const adjustWeekday = (weekday: CalendarDays) => weekday > 0 ? weekday - 1 : 6;

// from iterMonthDates of node-calendar package :
// create an array of Date objects for a given month, with complete weeks
export const iterMonthDates = (year: number, month: number) => {
  if (month < 1 || month > 12) {
    throw new IllegalMonthError();
  }

  const date = new Date(year, month - 1, 1);
  const day = adjustWeekday(date.getDay());
  const days = (day - FIRST_WEEK_DAY)  >= 0 ? (day - FIRST_WEEK_DAY) % 7 : 7 + (day - FIRST_WEEK_DAY);

  date.setTime(date.getTime() - (days * MILLISECONDS_PER_DAY));

  const dates = [];
  const run = true;
  while (run) {
    dates.push(new Date(date.getTime()));

    const currentDate = date.getDate();
    date.setTime(date.getTime() + MILLISECONDS_PER_DAY);

    // Hack to account for DST
    while(date.getDate() === currentDate) {
      date.setTime(date.getTime() + MILLISECONDS_PER_HOUR);
    }

    // run = (date.getMonth() !== month - 1 && adjustWeekday(date.getDay()) === FIRST_WEEK_DAY);
    if (date.getMonth() !== month - 1 && adjustWeekday(date.getDay()) === FIRST_WEEK_DAY) {
      break;
    }
  }

  return dates;
}

// from monthdatescalendar of node-calendar package : 
// create a matrix for a given month where rows are weeks and columns are days, days entries contain Date objects
export const createMonthDatesCalendarTable = (year: number, month: number) => {
  const days = [];
  const dates = iterMonthDates(year, month);
  for (let i = 0; i < dates.length; i += 7) {
    days.push(dates.slice(i, i + 7));
  }

  return days;
}

export const getFullWeekMonthDates = (date: Moment): Moment[][] => {
  const dates = createMonthDatesCalendarTable(
    date.year(),
    date.month() + 1,
  );

  return dates.map((week) => week.map((value : Date) => moment(value)));
}

export const CalendarUtils = {
  isEventOwner,
  isSameDate,
  isSameMonth,
  isToday,
  formatDateToDayInMonthString,
  formatDateToISOString,
  formatDateToMonthAndYearString,
  formatDateToDayFullMonthAndYearString,
  formatDateToHourString,
  adjustWeekday,
  iterMonthDates,
  createMonthDatesCalendarTable,
  getFullWeekMonthDates
};

export default CalendarUtils;